import axios from 'axios'

interface ChunkedFileResponse {
    content: string
    chunkIndex: number
    totalChunks: number
    totalBytes: number
    chunkBytes: number
    lastChunk: boolean
}

interface FetchVersionFileOptions {
    onProgress?: (loaded: number, total: number) => void
}

/**
 * Fetches a version file using chunked responses.
 * The endpoint returns files in chunks (default 5MB each) to work around API Gateway size limits.
 * This function fetches all chunks sequentially and concatenates them to return the full content.
 */
export async function fetchVersionFile(
    versionId: string,
    file: string,
    options?: FetchVersionFileOptions
): Promise<string> {
    const chunks: string[] = []
    let chunkIndex = 0
    let totalChunks = 1
    let loadedBytes = 0
    let totalBytes = 0

    while (chunkIndex < totalChunks) {
        const {data} = await axios.post<ChunkedFileResponse>('/api/admin/version/file', {
            versionId,
            file,
            chunkIndex
        })

        chunks.push(data.content)
        totalChunks = data.totalChunks
        totalBytes = data.totalBytes
        loadedBytes += data.chunkBytes

        options?.onProgress?.(loadedBytes, totalBytes)
        chunkIndex++
    }

    return chunks.join('')
}

/**
 * Fetches a version file and parses it as JSON.
 * Handles both the chunked response format and legacy string responses for backwards compatibility.
 */
export async function fetchVersionFileAsJson<T = any>(
    versionId: string,
    file: string,
    options?: FetchVersionFileOptions
): Promise<T> {
    const content = await fetchVersionFile(versionId, file, options)
    return JSON.parse(content)
}
